/**
 * Location and Time Selector Component
 *
 * Handles location and time selection for the Object Finder.
 * Reuses functionality from the main calculator including geolocation and timezone handling.
 */

export class LocationTimeSelector {
    constructor() {
        this.isInitialized = false;
        this.currentLocation = {
            latitude: 42.0,
            longitude: -6.0
        };
        this.currentDate = new Date().toISOString().split('T')[0];
        this.callbacks = {
            onLocationChange: null,
            onDateChange: null
        };
    }

    /**
     * Initialize the location and time selector
     */
    initialize() {
        if (this.isInitialized) {
            return;
        }

        try {
            // Set up UI first to ensure date is always set
            this.setupUI();

            // Set up event listeners (may fail if elements not found)
            this.setupEventListeners();

            this.isInitialized = true;
            console.log('Location Time Selector initialized');
        } catch (error) {
            console.error('Failed to initialize Location Time Selector:', error);

            // Even if initialization fails, try to set the date as a fallback
            try {
                const dateInput = document.getElementById('observationDate');
                if (dateInput && !dateInput.value) {
                    dateInput.value = this.currentDate;
                }
            } catch (dateError) {
                console.error('Failed to set default date:', dateError);
            }
        }
    }

    /**
     * Set up event listeners for location and time controls
     */
    setupEventListeners() {
        try {
            // Location inputs
            const latInput = document.getElementById('observerLatitude');
            if (latInput) {
                latInput.addEventListener('input', (e) => {
                    this.currentLocation.latitude = parseFloat(e.target.value) || 42.0;
                    this.notifyLocationChange();
                });
            }

            const lonInput = document.getElementById('observerLongitude');
            if (lonInput) {
                lonInput.addEventListener('input', (e) => {
                    this.currentLocation.longitude = parseFloat(e.target.value) || -6.0;
                    this.notifyLocationChange();
                });
            }

            // Date input
            const dateInput = document.getElementById('observationDate');
            if (dateInput) {
                dateInput.addEventListener('change', (e) => {
                    this.currentDate = e.target.value;
                    this.notifyDateChange();
                });
            }
        } catch (error) {
            console.error('Error setting up basic event listeners:', error);
        }

        // Use My Location button (optional feature)
        try {
            this.setupUseMyLocationButton();
        } catch (error) {
            console.warn('Failed to set up Use My Location button:', error);
        }

        // Location presets (optional feature)
        try {
            this.addLocationPresets();
        } catch (error) {
            console.warn('Failed to add location presets:', error);
        }
    }

    /**
     * Set up the "Use My Location" button
     */
    setupUseMyLocationButton() {
        // Find or create the button
        let useMyLocationBtn = document.getElementById('useMyLocationBtn');

        if (!useMyLocationBtn) {
            // Create the button if it doesn't exist
            const latInput = document.getElementById('observerLatitude');
            if (latInput && latInput.parentNode) {
                const btnContainer = document.createElement('div');
                btnContainer.className = 'mt-2';
                btnContainer.innerHTML = `
                    <button id="useMyLocationBtn" type="button" class="btn btn-sm btn-outline-secondary">
                        <i class="fas fa-location-arrow"></i> Use My Location
                    </button>
                    <div id="nearestTownDisplay" class="form-text text-muted mt-1" style="min-height: 1.2em;"></div>
                `;
                latInput.parentNode.appendChild(btnContainer);
                useMyLocationBtn = document.getElementById('useMyLocationBtn');
            }
        }

        if (useMyLocationBtn) {
            useMyLocationBtn.addEventListener('click', () => this.handleUseMyLocation());
        }
    }

    /**
     * Handle "Use My Location" functionality
     */
    async handleUseMyLocation() {
        const btn = document.getElementById('useMyLocationBtn');
        const townEl = document.getElementById('nearestTownDisplay');

        try {
            if (!navigator.geolocation) {
                this.showError('Geolocation not supported by this browser.');
                return;
            }

            // Update button state
            if (btn) {
                btn.disabled = true;
                btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Locating...';
            }
            if (townEl) townEl.textContent = '';

            // Get current position
            const position = await this.getCurrentPosition();
            const lat = position.coords.latitude;
            const lon = position.coords.longitude;

            // Update location
            this.setLocation(lat, lon);

            // Try to get nearest town
            try {
                const town = await this.reverseGeocodeNearestTown(lat, lon);
                if (townEl && town) {
                    townEl.textContent = `Nearest: ${town}`;
                }
            } catch (error) {
                console.warn('Failed to get nearest town:', error);
                if (townEl) {
                    townEl.textContent = 'Location detected successfully';
                }
            }

        } catch (error) {
            this.handleGeolocationError(error);
        } finally {
            // Restore button state
            if (btn) {
                btn.disabled = false;
                btn.innerHTML = '<i class="fas fa-location-arrow"></i> Use My Location';
            }
        }
    }

    /**
     * Get current position as a Promise
     * @returns {Promise} Position object
     */
    getCurrentPosition() {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000
            });
        });
    }

    /**
     * Reverse geocode to get nearest town
     * @param {number} lat - Latitude
     * @param {number} lon - Longitude
     * @returns {Promise<string>} Town name
     */
    async reverseGeocodeNearestTown(lat, lon) {
        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&zoom=10&addressdetails=1`;

        const response = await fetch(url, {
            headers: { 'Accept': 'application/json' }
        });

        if (!response.ok) {
            throw new Error('Reverse geocode failed');
        }

        const data = await response.json();
        const addr = data && data.address ? data.address : {};

        // Prefer town/city/village/hamlet in order
        const locality = addr.town || addr.city || addr.village || addr.hamlet || addr.suburb || null;
        const state = addr.state || addr.region || null;
        const country = addr.country || null;

        const parts = [locality, state, country].filter(Boolean);
        return parts.join(', ');
    }

    /**
     * Handle geolocation errors
     * @param {Object} error - Geolocation error
     */
    handleGeolocationError(error) {
        let message = 'Failed to obtain location.';

        if (error && error.code) {
            switch (error.code) {
                case 1:
                    message = 'Location permission denied.';
                    break;
                case 2:
                    message = 'Position unavailable.';
                    break;
                case 3:
                    message = 'Geolocation timeout.';
                    break;
            }
        }

        this.showError(message);
    }

    /**
     * Add location presets
     */
    addLocationPresets() {
        const lonInput = document.getElementById('observerLongitude');
        if (lonInput && lonInput.parentNode) {
            // Create presets container
            const presetsContainer = document.createElement('div');
            presetsContainer.className = 'mt-2';
            presetsContainer.innerHTML = `
                <div class="dropdown">
                    <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                        <i class="fas fa-map-marked-alt"></i> Quick Locations
                    </button>
                    <ul class="dropdown-menu">
                        <li><a class="dropdown-item" href="#" data-lat="40.7" data-lon="-74.0">New York, USA</a></li>
                        <li><a class="dropdown-item" href="#" data-lat="51.5" data-lon="-0.1">London, UK</a></li>
                        <li><a class="dropdown-item" href="#" data-lat="35.7" data-lon="139.7">Tokyo, Japan</a></li>
                        <li><a class="dropdown-item" href="#" data-lat="-33.9" data-lon="151.2">Sydney, Australia</a></li>
                        <li><a class="dropdown-item" href="#" data-lat="48.9" data-lon="2.3">Paris, France</a></li>
                        <li><a class="dropdown-item" href="#" data-lat="52.5" data-lon="13.4">Berlin, Germany</a></li>
                        <li><a class="dropdown-item" href="#" data-lat="19.4" data-lon="-155.3">Mauna Kea, Hawaii</a></li>
                        <li><a class="dropdown-item" href="#" data-lat="-24.6" data-lon="-70.4">Atacama Desert, Chile</a></li>
                        <li><a class="dropdown-item" href="#" data-lat="28.3" data-lon="-16.5">Canary Islands, Spain</a></li>
                    </ul>
                </div>
                <small class="text-muted d-block mt-1">Popular observing locations</small>
            `;

            // Insert after longitude input
            lonInput.parentNode.appendChild(presetsContainer);

            // Add click handlers
            presetsContainer.querySelectorAll('a[data-lat]').forEach(link => {
                link.addEventListener('click', (e) => {
                    e.preventDefault();
                    const lat = parseFloat(e.target.getAttribute('data-lat'));
                    const lon = parseFloat(e.target.getAttribute('data-lon'));
                    this.setLocation(lat, lon);
                });
            });
        }
    }

    /**
     * Set up initial UI state
     */
    setupUI() {
        // Set current date as default
        const dateInput = document.getElementById('observationDate');
        if (dateInput && !dateInput.value) {
            dateInput.value = this.currentDate;
        }

        // Set default location values
        const latInput = document.getElementById('observerLatitude');
        const lonInput = document.getElementById('observerLongitude');

        if (latInput && !latInput.value) {
            latInput.value = this.currentLocation.latitude;
        }
        if (lonInput && !lonInput.value) {
            lonInput.value = this.currentLocation.longitude;
        }

        // Add timezone info
        this.addTimezoneInfo();
    }

    /**
     * Add timezone information display
     */
    addTimezoneInfo() {
        const dateInput = document.getElementById('observationDate');
        if (dateInput && dateInput.parentNode) {
            // Check if timezone info already exists
            let timezoneInfo = document.getElementById('timezoneInfo');
            if (!timezoneInfo) {
                timezoneInfo = document.createElement('div');
                timezoneInfo.id = 'timezoneInfo';
                timezoneInfo.className = 'form-text text-muted mt-1';
                dateInput.parentNode.appendChild(timezoneInfo);
            }

            // Update timezone info
            this.updateTimezoneInfo();
        }
    }

    /**
     * Update timezone information
     */
    updateTimezoneInfo() {
        const timezoneInfo = document.getElementById('timezoneInfo');
        if (timezoneInfo) {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const now = new Date();
            const offset = -now.getTimezoneOffset() / 60;
            const offsetStr = offset >= 0 ? `+${offset}` : `${offset}`;

            timezoneInfo.innerHTML = `
                <i class="fas fa-clock"></i>
                Local timezone: ${timezone} (UTC${offsetStr})
            `;
        }
    }

    /**
     * Set location
     * @param {number} latitude - Latitude in degrees
     * @param {number} longitude - Longitude in degrees
     */
    setLocation(latitude, longitude) {
        this.currentLocation.latitude = latitude;
        this.currentLocation.longitude = longitude;

        // Update input fields
        const latInput = document.getElementById('observerLatitude');
        const lonInput = document.getElementById('observerLongitude');

        // Force dot decimal separator (some locales use comma)
        if (latInput) latInput.value = latitude.toFixed(4).replace(',', '.');
        if (lonInput) lonInput.value = longitude.toFixed(4).replace(',', '.');

        this.notifyLocationChange();
    }

    /**
     * Set observation date
     * @param {string} date - Date in YYYY-MM-DD format
     */
    setDate(date) {
        this.currentDate = date;

        // Update input field
        const dateInput = document.getElementById('observationDate');
        if (dateInput) dateInput.value = date;

        this.notifyDateChange();
    }

    /**
     * Get current location
     * @returns {Object} Current location
     */
    getCurrentLocation() {
        return { ...this.currentLocation };
    }

    /**
     * Get current date
     * @returns {string} Current date
     */
    getCurrentDate() {
        return this.currentDate;
    }

    /**
     * Set callback for location changes
     * @param {Function} callback - Callback function
     */
    onLocationChange(callback) {
        this.callbacks.onLocationChange = callback;
    }

    /**
     * Set callback for date changes
     * @param {Function} callback - Callback function
     */
    onDateChange(callback) {
        this.callbacks.onDateChange = callback;
    }

    /**
     * Notify about location changes
     */
    notifyLocationChange() {
        if (this.callbacks.onLocationChange) {
            this.callbacks.onLocationChange(this.getCurrentLocation());
        }
    }

    /**
     * Notify about date changes
     */
    notifyDateChange() {
        if (this.callbacks.onDateChange) {
            this.callbacks.onDateChange(this.currentDate);
        }
    }

    /**
     * Validate current location and date
     * @returns {Object} Validation result
     */
    validate() {
        const errors = [];

        // Validate latitude
        if (isNaN(this.currentLocation.latitude) ||
            this.currentLocation.latitude < -90 ||
            this.currentLocation.latitude > 90) {
            errors.push('Latitude must be between -90 and 90 degrees');
        }

        // Validate longitude
        if (isNaN(this.currentLocation.longitude) ||
            this.currentLocation.longitude < -180 ||
            this.currentLocation.longitude > 180) {
            errors.push('Longitude must be between -180 and 180 degrees');
        }

        // Validate date
        if (!this.currentDate) {
            errors.push('Observation date is required');
        } else {
            const date = new Date(this.currentDate);
            if (isNaN(date.getTime())) {
                errors.push('Invalid observation date');
            }
        }

        return {
            valid: errors.length === 0,
            errors: errors
        };
    }

    /**
     * Show error message
     * @param {string} message - Error message
     */
    showError(message) {
        // Look for global error display function
        if (typeof window.showError === 'function') {
            window.showError(message);
        } else {
            // Fallback to console and alert
            console.error('Location Time Selector Error:', message);
            alert(message);
        }
    }

    /**
     * Get location description for display
     * @returns {string} Location description
     */
    getLocationDescription() {
        const lat = this.currentLocation.latitude.toFixed(2);
        const lon = this.currentLocation.longitude.toFixed(2);
        const latDir = this.currentLocation.latitude >= 0 ? 'N' : 'S';
        const lonDir = this.currentLocation.longitude >= 0 ? 'E' : 'W';

        return `${Math.abs(lat)}°${latDir}, ${Math.abs(lon)}°${lonDir}`;
    }
}

export default LocationTimeSelector;